/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useEffect, useState } from 'react';
import type { OptionProps, OptionTypeBase } from 'react-select';
import { components } from 'react-select';
import { useI18n } from '@whizz/react-i18n';
import type { RecentDateOptionsType, SinceDateOptionsType } from '../../pages/types';
import { recent_date, since_date } from '../../pages/types';
import type { ToggleModalProps } from '../../components/modal/types';
import { useModal } from '../useModal';
import { Alert, Dropdown, Toggle } from '../../components';
import { CustomOptionWithIcon } from '../../components/selector/components';
import { GLOBAL_ICONS } from '../../util/vars';
import { useLocalStorage } from 'react-use';
import classNames from 'classnames';
import { InfoButton } from '../../global';

interface DayType {
  day: SinceDateOptionsType;
}

interface DaySelectorModalProps extends Pick<ToggleModalProps, 'toggle'> {
  initialValues: DayType;
  onSubmit(values: DayType): Promise<void>;
}

type ValueType = { value: RecentDateOptionsType, label: string } | null;
type DisplayValueType = { value: RecentDateOptionsType, label: string } | null;
type CustomType = { value: SinceDateOptionsType, label:string } | null;

const custom_id = 'since_x';

export const useRecentDateSelector = (username: string): [
  JSX.Element,
  { value: RecentDateOptionsType, label: string } | null,
  { value: SinceDateOptionsType, label:string } | null,
  boolean,
] => {

  const getOption = (value: RecentDateOptionsType) => {
    return {
      value,
      label: translate(`components.SELECTORS.DATE.${value}`),
    };
  };

  const getOptions = (): { value: RecentDateOptionsType, label: string }[] => {
    return recent_date.map((range) => {
      return getOption(range);
    });
  };

  const { translate } = useI18n();
  const [ storedValues, setStoredValues ] = useLocalStorage<{ value: ValueType, displayValue: DisplayValueType, customValue: CustomType }>(`recentdate_${username}`, { value: null, displayValue: null, customValue: null });
  const [ value, setValue ] = useState<ValueType>(storedValues?.value ? getOption(storedValues?.value.value) : null);
  const [ displayValue, setDisplayValue ] = useState<DisplayValueType>(storedValues?.displayValue ? getOption(storedValues?.displayValue.value) : null);
  const [ customValue, setCustomValue ] = useState<CustomType>(storedValues?.customValue ?? null);
  const [ autoFetch, setAutoFetch ] = useState(false);
  const [ showInfo, setShowInfo ] = useState(false);

  const [ modal, toggle, setProps ] = useModal((props: DaySelectorModalProps & Pick<ToggleModalProps, 'toggle'>) => (
    <DaySelector {...props} />
  ), {
    title: translate('components.SELECTORS.DATE.since_x'),
    size: 'sm',
    onSubmit: (values: DayType) => {
      const newCustomValue = {
        value: values.day,
        label: translate(`components.SELECTORS.DATE.SINCE.${values.day}`),
      };
      // Todo: Improve this as setting the custom date causes a bug where the "value" ("since_x") is (briefly) incorrect.
      const option = getOption(recent_date[1]);
      setValue(option);
      setDisplayValue(option);
      setCustomValue(newCustomValue);
    },
    closeCallback: () => resetValue(),
  }, () => resetValue());

  const resetValue = (): void => {
    if (customValue) {
      return;
    }
    defaultValue();
  };

  const defaultValue = () => {
    const option = getOption(recent_date[1]);
    setValue(option);
    setDisplayValue(option);
    const newCustomValue = {
      value: since_date[0],
      label: translate(`components.SELECTORS.DATE.SINCE.${since_date[0]}`),
    };
    setCustomValue(newCustomValue);
  };

  const getCustomRangeString = (): string | null => {
    if (customValue) {
      return customValue.label;
    }

    return null;
  };


  useEffect(() => {
    if (customValue) {
      setProps((prev) => ({
        ...prev,
        initialValues: {
          day: customValue.value,
        },
      }));
    }
  }, [ customValue ]);

  useEffect(() => {
    if (!value) {
      defaultValue();
    }
  }, [ value ]);

  useEffect(() => {
    setStoredValues({ value, customValue, displayValue });
  }, [ value, customValue, displayValue ]);

  const toggleHandler = () => setAutoFetch((prev) => !prev);

  const selectorComponent = (
    <>
      {modal}
      <div className={'md:flex mx-auto space-y-2 md:space-y-0 md:space-x-2'}>
        <Dropdown
          menuIsOpen
          id={'recent_date_selector'}
          className={'mx-auto'}
          align={'space-between'}
          label={'Time range'}
          hideLabel
          fullWidthOnMobile
          forceBorder
          disableSearch={true}
          icon={GLOBAL_ICONS.calendar}
          buttonPlaceholder={translate('components.SELECTORS.PLACEHOLDERS.DATE_RANGE')}
          options={getOptions()}
          value={displayValue}
          customValueToShow={getCustomRangeString()}
          handleChange={(option: { value: RecentDateOptionsType, label: string }) => {
            setDisplayValue(option);
            if (option.value === custom_id) {
              toggle(true);
            } else {
              setValue(option);
              setCustomValue(null);
            }
          }}
          componentsForSelect={{
            Option: (props: OptionProps<OptionTypeBase, false>) => {
              if (props.data.value === custom_id) {
                return (
                  <CustomOptionWithIcon
                    icon={GLOBAL_ICONS.calendar}
                    size={36}
                    {...props}
                  >
                    {props.children}
                    {getCustomRangeString() !== null ? (
                      <div className={'text-sm'}>
                        {getCustomRangeString()}
                      </div>
                    ) : ''}
                  </CustomOptionWithIcon>
                );
              }
              return (
                <components.Option {...props} />
              );
            },
          }}
        />
        <div className={'w-full md:w-auto inline-flex items-center justify-end space-x-2'}>
          <div className='ml-4 mr-0.5'>
            <Toggle
              id={'update'}
              label={translate('pages.HOME.AUTO_FETCH.LABEL')}
              color={'header'}
              checked={autoFetch}
              isOnOff
              onChange={toggleHandler}
            />
          </div>
          <button className={'appearance-none'} onClick={toggleHandler}>
            <div className={`text-sm font-bold transition-opacity ${classNames({ 'opacity-50' : !autoFetch })}`}>{translate('pages.HOME.AUTO_FETCH.LABEL')}</div>
          </button>
          <InfoButton onClick={() => setShowInfo((prev) => !prev)} />
        </div>
      </div>
      <Alert disableIcon show={showInfo} className={'my-md'}>
        {translate('pages.HOME.AUTO_FETCH.INFO')}
      </Alert>
    </>
  );

  return [ selectorComponent, value, customValue, autoFetch ];
};

const DaySelector = ({ initialValues, onSubmit, toggle }: DaySelectorModalProps) => {
  const { translate } = useI18n();

  const handleChange = (e: React.MouseEvent<HTMLInputElement>) => {
    const value = {
      day: e.currentTarget.value as DayType['day'],
    };
    return Promise.resolve(onSubmit(value)).then(() => {
      toggle(false);
    });
  };

  return (
    <ul className={'container'}>
      {since_date.map((date) => (
        <li key={date} className={'my-2'}>
          <label>
            <input
              type={'radio'}
              name={'day'}
              value={date}
              defaultChecked={initialValues?.day === date}
              onClick={handleChange}
            /> {translate(`components.SELECTORS.DATE.DAYS.${date}`)}
          </label>
        </li>
      ))}
    </ul>
  );
};
